// KEK wrap/unwrap for the §7.1 identity-bundle `wraps` envelopes (strategy
// doc §9.1). Shared by the browser pages and the headless dev-cli twin — the
// credential and derived KEK live only in the calling process's memory.
//
// Wrap kinds (all share the same XChaCha20-Poly1305 AEAD, ad = null; only the
// KEK *source* differs, so the frozen interop vectors keep holding):
//   - wraps.password  — Argon2id(password). RETIRED for new accounts in the
//     keystore+recovery model (AWS_MIGRATION.md credential section); kept for
//     existing dev-loop accounts.
//   - wraps.recovery  — Argon2id(normalized recovery code), same envelope
//     shape as password (see shared/recovery.js).
//   - wraps.webauthn[] — one per registered passkey; KEK derived from the
//     WebAuthn PRF extension output (no Argon2id — the PRF output is already
//     a full-entropy secret minted inside the authenticator).

/**
 * Wrap the identity secrets under an Argon2id KEK derived from the password.
 * Tries MODERATE limits first (the agreed default); falls back to INTERACTIVE
 * if the WASM heap can't take 256 MiB. The parameters used are recorded in
 * the envelope, so unwrapping never guesses.
 */
export function wrapSecrets(sodium, password, secretsBytes) {
  const salt = sodium.randombytes_buf(sodium.crypto_pwhash_SALTBYTES);
  const attempts = [
    { ops: sodium.crypto_pwhash_OPSLIMIT_MODERATE, mem: sodium.crypto_pwhash_MEMLIMIT_MODERATE },
    { ops: sodium.crypto_pwhash_OPSLIMIT_INTERACTIVE, mem: sodium.crypto_pwhash_MEMLIMIT_INTERACTIVE },
  ];
  let lastErr = null;
  for (const { ops, mem } of attempts) {
    try {
      const kek = sodium.crypto_pwhash(32, password, salt, ops, mem, sodium.crypto_pwhash_ALG_ARGON2ID13);
      const nonce = sodium.randombytes_buf(sodium.crypto_aead_xchacha20poly1305_ietf_NPUBBYTES);
      const ct = sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(secretsBytes, null, null, nonce, kek);
      sodium.memzero(kek);
      if (ops !== attempts[0].ops) console.warn('Argon2id fell back to INTERACTIVE limits (WASM memory)');
      return {
        salt: sodium.to_base64(salt, sodium.base64_variants.ORIGINAL),
        opslimit: ops,
        memlimit: mem,
        nonce: sodium.to_base64(nonce, sodium.base64_variants.ORIGINAL),
        ciphertext: sodium.to_base64(ct, sodium.base64_variants.ORIGINAL),
      };
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr;
}

// Domain separation for the PRF→KEK derivation: a keyed BLAKE2b-32 so the
// wrap KEK can never collide with any other use of the same PRF output.
// (Key must be 16–64 bytes for crypto_generichash; this is 19.)
const WEBAUTHN_KEK_CONTEXT = 'nc-e2ee/webauthn/v1';

function deriveWebauthnKek(sodium, prfOutput) {
  if (!(prfOutput instanceof Uint8Array) || prfOutput.length < 32) {
    throw new Error('WebAuthn PRF output must be at least 32 bytes');
  }
  return sodium.crypto_generichash(32, prfOutput, new TextEncoder().encode(WEBAUTHN_KEK_CONTEXT));
}

/**
 * Wrap the identity secrets under a KEK derived from a WebAuthn PRF output.
 * `meta.credentialId` (base64 of the credential's rawId) and `meta.prfSalt`
 * (base64 of the salt passed to the PRF eval — fixed per account so unlock
 * can evaluate before knowing which credential answers) are recorded in the
 * envelope; the caller performs all WebAuthn interaction itself.
 */
export function wrapToWebauthn(sodium, prfOutput, secretsBytes, meta) {
  if (!meta?.credentialId || !meta?.prfSalt) {
    throw new Error('wrapToWebauthn requires meta.credentialId and meta.prfSalt');
  }
  const kek = deriveWebauthnKek(sodium, prfOutput);
  try {
    const nonce = sodium.randombytes_buf(sodium.crypto_aead_xchacha20poly1305_ietf_NPUBBYTES);
    const ct = sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(secretsBytes, null, null, nonce, kek);
    return {
      credentialId: meta.credentialId,
      prfSalt: meta.prfSalt,
      nonce: sodium.to_base64(nonce, sodium.base64_variants.ORIGINAL),
      ciphertext: sodium.to_base64(ct, sodium.base64_variants.ORIGINAL),
    };
  } finally {
    sodium.memzero(kek);
  }
}

/** Unwrap a §7.1 "webauthn" envelope with a fresh PRF output. Throws on mismatch. */
export function unwrapFromWebauthn(sodium, prfOutput, env) {
  const ORIG = sodium.base64_variants.ORIGINAL;
  const kek = deriveWebauthnKek(sodium, prfOutput);
  try {
    const bytes = sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(
      null, sodium.from_base64(env.ciphertext, ORIG), null, sodium.from_base64(env.nonce, ORIG), kek);
    return JSON.parse(new TextDecoder().decode(bytes));
  } finally {
    sodium.memzero(kek);
  }
}

/** Unwrap a §7.1 "password" envelope. Throws on wrong password. */
export function unwrapSecrets(sodium, password, env) {
  const ORIG = sodium.base64_variants.ORIGINAL;
  const kek = sodium.crypto_pwhash(
    32, password, sodium.from_base64(env.salt, ORIG), env.opslimit, env.memlimit,
    sodium.crypto_pwhash_ALG_ARGON2ID13);
  try {
    const bytes = sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(
      null, sodium.from_base64(env.ciphertext, ORIG), null, sodium.from_base64(env.nonce, ORIG), kek);
    return JSON.parse(new TextDecoder().decode(bytes));
  } finally {
    sodium.memzero(kek);
  }
}

// ---- invite keys (INVITE-LINK-PASSKEY-FIRST.md §1.2, 2026-10-08) -----------
// A per-(owner, recipient) member key the OWNER mints at invite time and
// hands over through a link whose URL fragment carries a one-time code. The
// code is 20 random bytes (full entropy), so the KEK is a keyed BLAKE2b of
// it — no Argon2id, cheap on phones — with the same AEAD as every other wrap.
// The service stores the envelope and never sees the code (fragments do not
// reach it); only the holder of the link can open it.
const INVITE_KEK_CONTEXT = 'nc-e2ee/invite/v1';

function deriveInviteKek(sodium, codeBytes) {
  if (!(codeBytes instanceof Uint8Array) || codeBytes.length < 16) {
    throw new Error('invite code must be at least 16 bytes');
  }
  return sodium.crypto_generichash(32, codeBytes, new TextEncoder().encode(INVITE_KEK_CONTEXT));
}

/**
 * Wrap the invite key secrets (any JSON-able object) under the one-time code.
 * Returns the `{ nonce, ciphertext }` envelope the service stores.
 */
export function wrapInviteKey(sodium, codeBytes, secretsObj) {
  const kek = deriveInviteKek(sodium, codeBytes);
  try {
    const nonce = sodium.randombytes_buf(sodium.crypto_aead_xchacha20poly1305_ietf_NPUBBYTES);
    const bytes = new TextEncoder().encode(JSON.stringify(secretsObj));
    const ct = sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(bytes, null, null, nonce, kek);
    return {
      nonce: sodium.to_base64(nonce, sodium.base64_variants.ORIGINAL),
      ciphertext: sodium.to_base64(ct, sodium.base64_variants.ORIGINAL),
    };
  } finally {
    sodium.memzero(kek);
  }
}

/** Unwrap an invite-key envelope with the code from the link. Throws on a wrong code or a tampered blob. */
export function unwrapInviteKey(sodium, codeBytes, env) {
  const ORIG = sodium.base64_variants.ORIGINAL;
  const kek = deriveInviteKek(sodium, codeBytes);
  try {
    const bytes = sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(
      null, sodium.from_base64(env.ciphertext, ORIG), null, sodium.from_base64(env.nonce, ORIG), kek);
    return JSON.parse(new TextDecoder().decode(bytes));
  } finally {
    sodium.memzero(kek);
  }
}
